/**
 * Lights.js — pooled dynamic FX lights.
 * owner: fx
 *
 * Muzzle flashes, hot metal impacts and explosions must put real light onto the
 * world, not just add a sprite. The pool is allocated once at init and lights
 * are never added to or removed from the scene afterwards — that keeps the
 * light count constant so materials are never recompiled mid-game.
 */
import * as THREE from 'three';

export class LightPool {
  constructor(scene, count = 4, opts = {}) {
    this.lights = [];
    this.state = [];
    this.head = 0;
    for (let i = 0; i < count; i++) {
      const l = new THREE.PointLight(0xffffff, 0, 8, opts.decay ?? 2);
      l.castShadow = false;
      // NEVER toggle .visible: three counts visible lights when building the
      // program cache key, so flipping it recompiles every material in the
      // scene (~90 programs, seconds of stall). Idle lights sit at intensity 0.
      l.visible = true;
      l.name = `fx_light_${i}`;
      l.userData.fx = true;
      scene.add(l);
      this.lights.push(l);
      this.state.push({ age: 0, life: 0, peak: 0, curve: 'flash' });
    }
  }

  /**
   * @param {THREE.Vector3} pos
   * @param {number[]|THREE.Color} color
   * @param {number} intensity  peak intensity (three "candela"-ish, physically-correct off)
   * @param {number} radius     distance falloff
   * @param {number} duration   seconds
   * @param {string} curve      'flash' (instant on, fast decay) | 'burn' (ramp + long tail)
   */
  flash(pos, color, intensity, radius, duration, curve = 'flash') {
    const i = this.head;
    this.head = (this.head + 1) % this.lights.length;
    const l = this.lights[i];
    l.position.copy(pos);
    if (Array.isArray(color)) l.color.setRGB(color[0], color[1], color[2]);
    else l.color.copy(color);
    l.distance = radius;
    const s = this.state[i];
    s.age = 0; s.life = duration; s.peak = intensity; s.curve = curve;
    l.intensity = intensity;
    return l;
  }

  update(dt) {
    for (let i = 0; i < this.lights.length; i++) {
      const s = this.state[i];
      const l = this.lights[i];
      if (s.life <= 0) continue;
      s.age += dt;
      const t = s.age / Math.max(1e-4, s.life);
      if (t >= 1) { s.life = 0; l.intensity = 0; continue; }
      if (s.curve === 'burn') {
        const rise = Math.min(1, t / 0.12);
        l.intensity = s.peak * rise * Math.pow(1 - t, 1.7);
      } else {
        l.intensity = s.peak * Math.pow(1 - t, 2.4);
      }
    }
  }

  reset() {
    for (const l of this.lights) l.intensity = 0;
    for (const s of this.state) { s.age = 0; s.life = 0; }
    this.head = 0;
  }

  dispose() {
    for (const l of this.lights) l.removeFromParent();
    this.lights.length = 0;
  }
}
